import React, { useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import DOMPurify from 'dompurify';
import { Button } from './ui/button';
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Quote, Link as LinkIcon, Image as ImageIcon, Undo, Redo, Sparkles, Loader2, Wand2, Table as TableIcon, HelpCircle, MousePointerClick, Link2, Zap, ChevronDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import authAxios, { API } from '../utils/authAxios';
import { AI_ASSISTANCE_TYPES, getAIPrompt, SITEMAP_INTERNO, TEMPLATES } from './blog/AIBlogAssistant';

export function RichTextEditor({ content, onChange, placeholder = 'Escribe el contenido del artículo...', onSEOFieldsGenerated }) {
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiType, setAiType] = useState('articulo');
  const [aiLoading, setAiLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [showQuickInsert, setShowQuickInsert] = useState(false);
  const [showLinksPanel, setShowLinksPanel] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [2, 3, 4],
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full rounded-lg',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-emerald-400 underline',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'w-full border-collapse my-6',
        },
      }),
      TableRow,
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-slate-700 p-3',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-slate-600 p-3 bg-slate-800 text-left font-semibold',
        },
      }),
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none min-h-[300px] focus:outline-none p-4',
      },
    },
  });

  // Update editor content when the content prop changes (for editing existing articles)
  useEffect(() => {
    if (editor && content !== undefined && content !== null) {
      const currentContent = editor.getHTML();
      // Check if we need to update - the content is different and not just empty paragraphs
      const isCurrentEmpty = !currentContent || currentContent === '<p></p>' || currentContent === '';
      const isNewContentMeaningful = content && content.length > 0 && content !== '<p></p>';
      
      if (isNewContentMeaningful && content !== currentContent) {
        // Small delay to ensure editor is ready
        setTimeout(() => {
          editor.commands.setContent(content);
        }, 100);
      } else if (!isNewContentMeaningful && !isCurrentEmpty) {
        // Clear editor if new content is empty but editor has content
        editor.commands.setContent('');
      }
    }
  }, [content, editor]);

  if (!editor) return null;

  function addImage() {
    const url = window.prompt('URL de la imagen:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }

  function addLink() {
    const url = window.prompt('URL del enlace:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  }

  async function handleAIGenerate() {
    if (!aiPrompt.trim() && !['enlaces', 'mejorar'].includes(aiType)) {
      toast.error('Por favor describe qué contenido quieres crear');
      return;
    }

    // For 'enlaces' and 'mejorar', use current content
    const currentContent = editor.getHTML();
    if (['enlaces', 'mejorar'].includes(aiType) && (!currentContent || currentContent === '<p></p>')) {
      toast.error('Necesitas tener contenido en el editor para usar esta función');
      return;
    }

    setAiLoading(true);
    setGeneratedContent('');

    // Show info toast for article generation (takes longer)
    if (aiType === 'articulo') {
      toast.info('Generando artículo completo... Esto puede tardar 1-2 minutos', { duration: 10000 });
    }

    try {
      const { system, user } = getAIPrompt(aiType, aiPrompt, currentContent);
      
      // Use longer timeout for AI generation (3 minutes)
      const response = await authAxios.post(API + '/ai/generate-blog-content', {
        prompt: user,
        system_prompt: system,
        type: aiType,
        current_content: currentContent
      }, { 
        withCredentials: true,
        timeout: 180000 // 3 minutes timeout for AI generation
      });

      if (response.data.content) {
        setGeneratedContent(response.data.content);
        
        // If SEO fields were generated (for full article), notify parent
        if (response.data.has_seo_fields && onSEOFieldsGenerated) {
          onSEOFieldsGenerated({
            title: response.data.title || '',
            slug: response.data.slug || '',
            meta_description: response.data.meta_description || '',
            meta_keywords: response.data.meta_keywords || '',
            category: response.data.category || 'Guías'
          });
          toast.success('Artículo completo generado con título, meta y keywords');
        } else {
          toast.success('Contenido generado exitosamente');
        }
      }
    } catch (error) {
      console.error('Error generating content:', error);
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('La generación tardó demasiado. Intenta con un tema más específico o usa "Crear tabla" o "Crear FAQ" que son más rápidos.');
      } else {
        toast.error('Error al generar contenido. Intenta de nuevo.');
      }
    } finally {
      setAiLoading(false);
    }
  }

  function handleInsertContent() {
    if (generatedContent) {
      if (['articulo', 'mejorar', 'enlaces'].includes(aiType)) {
        editor.commands.setContent(generatedContent);
      } else {
        // For table, faq, cta - append to existing content
        editor.commands.insertContent(generatedContent);
      }
      onChange(editor.getHTML());
      setShowAIDialog(false);
      setGeneratedContent('');
      setAiPrompt('');
      toast.success('Contenido insertado');
    }
  }

  // Insert template directly
  function insertTemplate(templateKey) {
    const template = TEMPLATES[templateKey];
    if (template) {
      editor.commands.insertContent(template);
      onChange(editor.getHTML());
      setShowQuickInsert(false);
      toast.success('Plantilla insertada - edita los valores entre corchetes');
    }
  }

  // Insert internal link
  function insertInternalLink(linkData) {
    editor.chain().focus().setLink({ href: linkData.url }).insertContent(linkData.texto).run();
    onChange(editor.getHTML());
    setShowLinksPanel(false);
    toast.success('Enlace interno agregado');
  }

  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden bg-slate-950">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 border-b border-slate-700 bg-slate-900">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'bg-slate-700' : 'hover:bg-slate-800'}
        >
          <Bold className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'bg-slate-700' : 'hover:bg-slate-800'}
        >
          <Italic className="w-4 h-4" />
        </Button>
        <div className="w-px h-6 bg-slate-700 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editor.isActive('heading', { level: 2 }) ? 'bg-slate-700' : 'hover:bg-slate-800'}
        >
          <Heading2 className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={editor.isActive('heading', { level: 3 }) ? 'bg-slate-700' : 'hover:bg-slate-800'}
        >
          <Heading3 className="w-4 h-4" />
        </Button>
        <div className="w-px h-6 bg-slate-700 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'bg-slate-700' : 'hover:bg-slate-800'}
        >
          <List className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'bg-slate-700' : 'hover:bg-slate-800'}
        >
          <ListOrdered className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editor.isActive('blockquote') ? 'bg-slate-700' : 'hover:bg-slate-800'}
        >
          <Quote className="w-4 h-4" />
        </Button>
        <div className="w-px h-6 bg-slate-700 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addLink}
          className={editor.isActive('link') ? 'bg-slate-700' : 'hover:bg-slate-800'}
        >
          <LinkIcon className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={addImage}
          className="hover:bg-slate-800"
        >
          <ImageIcon className="w-4 h-4" />
        </Button>
        <div className="w-px h-6 bg-slate-700 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="hover:bg-slate-800 disabled:opacity-50"
        >
          <Undo className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="hover:bg-slate-800 disabled:opacity-50"
        >
          <Redo className="w-4 h-4" />
        </Button>
        
        {/* Quick Insert Templates */}
        <div className="w-px h-6 bg-slate-700 mx-1" />
        <div className="relative">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowQuickInsert(!showQuickInsert)}
            className="hover:bg-slate-800 gap-1"
          >
            <TableIcon className="w-4 h-4" />
            <ChevronDown className="w-3 h-3" />
          </Button>
          {showQuickInsert && (
            <div className="absolute top-full left-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 w-48">
              <div className="p-2 space-y-1">
                <button 
                  onClick={() => insertTemplate('tablaComparativa')}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700 rounded flex items-center gap-2"
                >
                  <TableIcon className="w-4 h-4 text-blue-400" />
                  Tabla comparativa
                </button>
                <button 
                  onClick={() => insertTemplate('faq')}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700 rounded flex items-center gap-2"
                >
                  <HelpCircle className="w-4 h-4 text-amber-400" />
                  Sección FAQ
                </button>
                <button 
                  onClick={() => insertTemplate('cta')}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700 rounded flex items-center gap-2"
                >
                  <MousePointerClick className="w-4 h-4 text-emerald-400" />
                  Botón CTA
                </button>
                <button 
                  onClick={() => insertTemplate('listaConIconos')}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700 rounded flex items-center gap-2"
                >
                  <List className="w-4 h-4 text-purple-400" />
                  Lista destacada
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Internal Links Panel */}
        <div className="relative">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowLinksPanel(!showLinksPanel)}
            className="hover:bg-slate-800 gap-1"
          >
            <Link2 className="w-4 h-4" />
            <ChevronDown className="w-3 h-3" />
          </Button>
          {showLinksPanel && (
            <div className="absolute top-full left-0 mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 w-64 max-h-80 overflow-y-auto">
              <div className="p-2 space-y-1">
                <div className="px-3 py-1 text-xs text-slate-400 font-medium">Enlaces internos del sitio</div>
                {Object.entries(SITEMAP_INTERNO).map(([key, data]) => (
                  <button 
                    key={key}
                    onClick={() => insertInternalLink(data)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-700 rounded"
                  >
                    <div className="text-slate-200">{data.texto}</div>
                    <div className="text-xs text-slate-500">{data.url}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* AI Button */}
        <div className="w-px h-6 bg-slate-700 mx-1" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowAIDialog(true)}
          className="hover:bg-emerald-900/50 text-emerald-400 gap-1"
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-xs">IA</span>
        </Button>
      </div>

      {/* AI Dialog */}
      <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-emerald-400" />
              Asistente IA para Blog - SinAdicciones.org
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* AI Type Selection */}
            <div>
              <Label className="mb-2 block">¿Qué quieres crear?</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {AI_ASSISTANCE_TYPES.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setAiType(type.id)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      aiType === type.id 
                        ? 'border-emerald-500 bg-emerald-500/10' 
                        : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
                    }`}
                  >
                    <div className="text-lg mb-1">{type.icon}</div>
                    <div className="font-medium text-sm text-slate-200">{type.label}</div>
                    <div className="text-xs text-slate-400 mt-1">{type.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Prompt Input */}
            {!['enlaces', 'mejorar'].includes(aiType) && (
              <div>
                <Label>Describe lo que necesitas</Label>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={AI_ASSISTANCE_TYPES.find(t => t.id === aiType)?.placeholder || 'Describe el contenido...'}
                  rows={3}
                  className="mt-1"
                />
              </div>
            )}

            {/* Info box for certain types */}
            {['enlaces', 'mejorar'].includes(aiType) && (
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-sm text-slate-400">
                  {aiType === 'enlaces' 
                    ? '🔗 La IA analizará el contenido actual y agregará enlaces a páginas relevantes del sitio (centros, profesionales, herramientas, etc.)'
                    : '✨ La IA mejorará el SEO del contenido actual: agregará keywords, mejorará títulos, expandirá secciones cortas y agregará enlaces internos.'
                  }
                </p>
              </div>
            )}

            {/* Site knowledge info */}
            <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
              <div className="flex items-start gap-2">
                <Zap className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-emerald-400 font-medium">La IA conoce tu sitio</p>
                  <p className="text-slate-400 mt-1">
                    Puede crear contenido con enlaces a: directorio de centros, profesionales, 
                    calculadora de costos, tests de evaluación, página de planes y contacto.
                  </p>
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <Button 
              onClick={handleAIGenerate} 
              disabled={aiLoading || (!aiPrompt.trim() && !['enlaces', 'mejorar'].includes(aiType))}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {aiLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {aiType === 'articulo' ? 'Generando artículo (1-2 min)...' : 'Generando contenido...'}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {aiType === 'articulo' ? 'Generar artículo completo' : 
                   aiType === 'tabla' ? 'Crear tabla' :
                   aiType === 'faq' ? 'Crear FAQ' :
                   aiType === 'cta' ? 'Crear CTA' :
                   aiType === 'enlaces' ? 'Agregar enlaces' :
                   'Mejorar contenido'}
                </>
              )}
            </Button>
            
            {/* Loading info for articles */}
            {aiLoading && aiType === 'articulo' && (
              <p className="text-xs text-amber-400 text-center">
                Los artículos completos tardan más porque incluyen tabla, FAQ y enlaces internos
              </p>
            )}

            {/* Generated Content Preview */}
            {generatedContent && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Contenido generado:</Label>
                  <span className="text-xs text-slate-400">
                    {generatedContent.length} caracteres
                  </span>
                </div>
                <div 
                  className="max-h-[300px] overflow-y-auto p-4 bg-slate-800 rounded-lg border border-slate-700 prose prose-invert prose-sm"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(generatedContent, { ADD_ATTR: ['target', 'rel'] }) }}
                />
                <div className="flex gap-2">
                  <Button 
                    onClick={handleInsertContent}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {['articulo', 'mejorar', 'enlaces'].includes(aiType) ? 'Reemplazar contenido' : 'Insertar al final'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setGeneratedContent('')}
                  >
                    Descartar
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Click outside to close dropdowns */}
      {(showQuickInsert || showLinksPanel) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => { setShowQuickInsert(false); setShowLinksPanel(false); }}
        />
      )}

      {/* Editor Content */}
      <EditorContent editor={editor} className="text-slate-200" />

      {/* Styles for placeholder */}
      <style>{`
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #64748b;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: #f8fafc;
        }
        .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
          color: #f8fafc;
        }
        .ProseMirror p {
          margin-bottom: 0.75rem;
          color: #cbd5e1;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .ProseMirror li {
          margin-bottom: 0.25rem;
        }
        .ProseMirror blockquote {
          border-left: 4px solid #10b981;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #94a3b8;
          font-style: italic;
        }
        .ProseMirror table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
        }
        .ProseMirror th {
          background-color: #1e293b;
          border: 1px solid #475569;
          padding: 0.75rem;
          text-align: left;
          font-weight: 600;
          color: #f8fafc;
        }
        .ProseMirror td {
          border: 1px solid #334155;
          padding: 0.75rem;
          color: #cbd5e1;
        }
        .ProseMirror tr:hover td {
          background-color: #1e293b;
        }
        .ProseMirror .tableWrapper {
          overflow-x: auto;
          margin: 1.5rem 0;
        }
      `}</style>
    </div>
  );
}
